/* eslint-disable unicorn/filename-case */
import { BaseKey, BaseRecord, CrudFilters } from '@refinedev/core';
import { SelectProps } from 'antd';
import { UIEvent, useCallback, useEffect, useState } from 'react';
import { useSelect } from '@refinedev/antd';

export const PAGE_SIZE = 20;
const SCROLL_THRESHOLD_IN_PX = 8;

interface Option {
  label: string;
  value: BaseKey;
}

interface UseProductableSelectProps {
  resource: string;
  /** Restringe a lista aos registros ainda não vinculados a um produto. */
  onlyWithoutProduct?: boolean;
  defaultValue?: BaseKey;
}

/** Acumula as páginas já carregadas sem repetir um item que volte em outra página. */
export const mergeOptions = (previous: Option[], incoming: Option[]) => {
  const known = new Set(previous.map(option => option.value));
  const fresh = incoming.filter(option => !known.has(option.value));

  return fresh.length > 0 ? [...previous, ...fresh] : previous;
};

/** O backend busca por nome ou por ID no mesmo filtro. */
export const buildSearchFilters = (value: string): CrudFilters =>
  value ? [{ field: 'filter[name]', operator: 'eq', value }] : [];

export const hasReachedBottom = (target: HTMLElement) =>
  target.scrollHeight - target.scrollTop - target.clientHeight <=
  SCROLL_THRESHOLD_IN_PX;

export const hasNextPage = (page: number, total: number) =>
  page * PAGE_SIZE < total;

/**
 * Select paginado para escolher o item vinculado a um produto (curso, pós, set,
 * combo ou item externo).
 *
 * O `useSelect` do refine busca só a primeira página e não oferece navegação,
 * então as opções são acumuladas conforme o usuário rola a lista. A busca é
 * feita no servidor, por nome ou por ID.
 */
export const useProductableSelect = ({
  resource,
  onlyWithoutProduct,
  defaultValue
}: UseProductableSelectProps) => {
  const [page, setPage] = useState(1);
  const [loadedOptions, setLoadedOptions] = useState<Option[]>([]);

  const permanentFilters: CrudFilters = onlyWithoutProduct
    ? [{ field: 'filter[without_product]', operator: 'eq', value: true }]
    : [];

  const { selectProps, queryResult } = useSelect<BaseRecord>({
    resource,
    optionLabel: 'name',
    optionValue: 'id',
    defaultValue,
    filters: permanentFilters,

    onSearch: value => {
      // Uma nova busca invalida o que já foi acumulado das páginas anteriores.
      setLoadedOptions([]);
      setPage(1);

      return buildSearchFilters(value);
    },

    pagination: {
      mode: 'server',
      current: page,
      pageSize: PAGE_SIZE
    }
  });

  const options = selectProps.options as Option[] | undefined;

  useEffect(() => {
    if (!options?.length) {
      return;
    }

    setLoadedOptions(previous => mergeOptions(previous, options));
  }, [options]);

  const total = Number(queryResult?.data?.total ?? 0);

  const onPopupScroll = useCallback(
    (event: UIEvent<HTMLElement>) => {
      const target = event.target as HTMLElement;

      if (
        hasReachedBottom(target) &&
        hasNextPage(page, total) &&
        !queryResult.isFetching
      ) {
        setPage(previous => previous + 1);
      }
    },
    [page, total, queryResult.isFetching]
  );

  const productableSelectProps: SelectProps = {
    ...selectProps,
    options: loadedOptions,
    onPopupScroll,
    placeholder: 'Busque pelo nome ou pelo ID'
  };

  return { selectProps: productableSelectProps };
};
